/**
 * B207/R14 — the JS binder for the native `TayroNativeAuth` plugin
 * (`local-plugins/tayro-native-auth`), an in-house `ASWebAuthenticationSession`
 * wrapper. Same discipline as `lib/billing/storeKit.ts` and `lib/appleAuth.ts`'s
 * own plugin binder: native-only, bound ONCE by jsName through
 * `@capacitor/core`'s `registerPlugin` so the WEB bundle never depends on the
 * plugin package, and the proxy comes back BOXED — see the thenable lesson
 * `appleAuth.ts` §0.7 documents at length. `return boundPlugin` bare is the
 * Build 7/8/9 hang, verbatim; never do it here either.
 *
 * `nativeAuth.ts`'s `openNativeOAuth` is the only caller. This module knows
 * nothing about Supabase, PKCE or error mapping — it only starts and cancels
 * a session and hands back whatever callback URL Apple verified.
 */

import { isNativeApp } from './platform';

export interface NativeAuthSessionPlugin {
  authenticate(options: {
    url: string;
    host: string;
    path: string;
    /** §4.1b contingency only — Build 33 never sends this (its own
     *  deployment target is 17.4, where the plugin's `#available` branch
     *  never needs it). Optional so this interface stays truthful about
     *  what Build 33 actually sends, rather than requiring a value nothing
     *  in this codebase ever supplies. */
    callbackScheme?: string;
    ephemeral?: boolean;
  }): Promise<{ url: string }>;
  cancel(): Promise<void>;
}

let boundPlugin: NativeAuthSessionPlugin | null = null;

/** Bind (once) and return the plugin proxy; null off-native or on bind
 *  failure. Wrapped in an object per the thenable lesson (`appleAuth.ts`
 *  §0.7) — the proxy itself must never be the resolved value. */
export async function getNativeAuthSessionPlugin(): Promise<{ session: NativeAuthSessionPlugin } | null> {
  if (!isNativeApp()) return null;
  if (boundPlugin) return { session: boundPlugin };
  try {
    const { registerPlugin } = await import('@capacitor/core');
    boundPlugin = registerPlugin<NativeAuthSessionPlugin>('TayroNativeAuth');
    return { session: boundPlugin };
  } catch {
    return null;
  }
}

/** Test-only: mirrors `__resetApplePluginForTest` / `__resetJoinLaunchUrlForTest`. */
export function __resetNativeAuthSessionPluginForTest(): void {
  boundPlugin = null;
}
